import { TrashIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { clearObjectUrl, getImageData } from '@/utils/file';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';

export function AvatarForm({
  form,
  fieldName,
  label = 'btn.update-avatar',
  currentAvatar,
  placeholder,
  description,
  accept = 'image/*',
  onDeleteAvatar = () => {},
}: {
  form: any;
  fieldName: string;
  currentAvatar: string | null | undefined;
  label?: string;
  placeholder?: string;
  description?: string;
  accept?: string;
  onDeleteAvatar?: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | undefined>(
    currentAvatar ?? undefined,
  );

  // Manually trigger refine verification on value files changed for fieldName
  const { trigger, watch } = form;
  useEffect(() => {
    const subscription = watch(() => {
      trigger([fieldName]);
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const updatePreview = (nextValue?: string) => {
    clearObjectUrl(preview);
    setPreview(nextValue);
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className="mb-4">
          <div className="flex flex-col gap-2 justify-center items-center">
            <div className="relative">
              {preview && (
                <Button
                  size="icon"
                  variant="outline"
                  className="absolute -top-6 -right-6 z-10"
                  onClick={(_) => {
                    updatePreview();
                    field.onChange(null);

                    onDeleteAvatar();
                  }}
                >
                  <TrashIcon />
                </Button>
              )}

              <Avatar className="w-[80px] h-[80px] rounded-md border z-0">
                <AvatarImage
                  src={preview}
                  alt="avatar"
                />

                <AvatarFallback className="text-2xl rounded-md">
                  {placeholder ?? ''}
                </AvatarFallback>
              </Avatar>
            </div>

            <Button
              onClick={(e) => {
                e.preventDefault();
                inputRef.current?.click();
              }}
            >
              {t(label)}
            </Button>

            <FormControl className="hidden">
              <Input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={(event) => {
                  const { files, previewUrl } = getImageData(event);

                  updatePreview(previewUrl);
                  field.onChange(files[0]);
                  event.target.value = '';
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
          </div>

          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
